import { useState } from "react";
import clsx from "clsx";

import { SummaryCollapse } from "./SummaryCollapse";
import { AIResponse } from "react-migration/components/AIResponse/AIResponse";
import { FeatureVoting } from "react-migration/components/FeatureVoting/FeatureVoting";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { SelectionFeature, SelectionType } from "src/js/stores/map/store";
import { SummaryType } from "react-migration/components/AIResponse/SummaryType";
import { Streamdown } from "streamdown";
import { useBetaFeatureFlag } from "react-migration/lib/user/useFeatureFlag";
import Feature from "src/js/stores/user/Feature";

export const Summary = ({
  content,
  selection,
  setDetailSelection,
}: {
  content: SummaryType[];
  selection: SelectionFeature;
  setDetailSelection: (selection: SelectionFeature) => void;
}) => {
  const useAiPlanningSummary = useBetaFeatureFlag(Feature.aiPlanningSummaryBeta);
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <>
      <div
        data-testid="summary-wrapper"
        className={clsx(
          "flex flex-col w-full transition-all ease-in-out duration-500",
          isExpanded ? "max-h-[2000px] overflow-y-auto" : "max-h-32 pb-8 overflow-hidden"
        )}
      >
        {/* Leverages the href data-href from the summary line to open the planning application details */}
        <div data-testid="summary-content-wrapper" className="px-1">
          {useAiPlanningSummary ? (
            <Streamdown
              components={{
                "planning-app": ({ id, children }) => {
                  return (
                    <a
                      className="cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        setDetailSelection({
                          id,
                          type: SelectionType.PLANNING_APPLICATION,
                        });
                      }}
                    >
                      {children}
                    </a>
                  );
                },
              }}
            >
              {content.map((item) => item.content).join("\n")}
            </Streamdown>
          ) : (
            <div
              onClick={({ target }) => {
                if (isAnchor(target)) {
                  setDetailSelection({
                    type: SelectionType.PLANNING_APPLICATION,
                    id: target.dataset.href,
                  });
                }
              }}
            >
              <AIResponse content={content} />
            </div>
          )}
        </div>
        <FeatureVoting
          featureName="AI Planning Summary"
          message={t("ai.vote.accuracy.title")}
          className="pb-2 px-8"
          selection={selection}
        />
      </div>
      <SummaryCollapse isExpanded={isExpanded} setIsExpanded={setIsExpanded} />
    </>
  );
};

function isAnchor(element: EventTarget): element is HTMLAnchorElement {
  return element instanceof HTMLAnchorElement;
}
